/** @jsxImportSource @emotion/react */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {css} from "@emotion/react";
import DarkThemeCss from '../components/DarkThemeCss';
import { useSelector, useDispatch } from 'react-redux';
import { Switch, Route, useHistory } from 'react-router-dom';
import { Button } from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { useSnackbar } from 'notistack';

import { useSocket } from "../context/useSocket";
//Authentication
import { useAuth0 } from "@auth0/auth0-react";

import '@fontsource/roboto';

import {
  selectUser,
  authenticateUserThunk
} from '../reducers/userSlice';

import '../styles/App.css';

import Routes from '../routes/Routes';
import { setIsLoadingData, setIsLoadingProjectsData } from '../reducers/optionsSlice';
import { addWells, getForecast, getWaterfloodForecast, updateWellStatus } from '../reducers/wellSlice';
import { formatUwi } from '../helpers/formatUwi';
import { colors } from '../helpers/colors';
import NavDrawer from './NavDrawer';
import { addProject, getProjects } from '../reducers/projectSlice';
import { addLists, makeListCurrent } from '../reducers/listSlice';
import { SignIn } from '../components/SignIn';
import { TermsConditions } from '../components/TermsConditions';
import { WellsContainer } from './WellsContainer';
import { ForecastContainer } from './ForecastContainer';
import uuid from 'react-uuid';
import { getTypeCurveForecast, saveTypeCurve } from '../reducers/typeCurveSlice';
import { addGroupWell, getGroupWellForecast, saveGroupWell } from '../reducers/groupWellSlice';
import { deleteFBA, saveFBA, updateFBA } from '../reducers/fbaSlice';

import { fetchCommodityPrices, getEconomics, updateEconomics } from '../reducers/economicsSlice';
import { fbaConversionFactors } from '../components/fba/fbaConversionFactors';
import { f_TO_C } from '../helpers/conversionFactors';
import { RTFOAdminContainer } from '../components/rtfo/RTFOAdminContainer';
import { RTFOContainerLoader } from './RTFOContainer';

const NODE_ENV = process.env.REACT_APP_NODE_ENV;

const App = () => {

  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const history = useHistory();


  const [userError, setUserError] = useState(false);
  const [signingIn, setSigningIn] = useState(true);

  
  const darkMode = user?.value?.darkMode || false;

  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const theme = createTheme({
    palette: {
      primary: {
        main: "#0758B7",
        dark: "#202C3E",
        light: "#d3ecf8"
      },
      secondary: {
        main: "#d32f2f",
      },
      black: {
        main: "#000",
      },
      toggle: {
        thumbOnColor: 'yellow',
        trackOnColor: 'red'
      },
      backgroundOne: {
        dark: '#121212',
        light: '#757575' // grey 600
      },
      backgroundTwo: {
        dark: '#212121', // grey 900
        light: '#e0e0e0' // grey 300
      },
      backgroundThree: {
        dark: '#212121', // grey 900
        light: '#f5f5f5' // grey 100
      },
      paper: {
        dark: '#263238',
        light: 'rgb(245, 245, 245)'
        // light: '#eee'
      },
      elevationOne: {
        dark: '#37474f',
        light: '#fff'
        // light: '#f5f5f5'
      },
      elevationTwo: {
        dark: '#455a64',
        light: 'rgb(240, 240, 240)'
        // light: '#e0e0e0'
      },
      mode: darkMode ? 'dark' : 'light'
    },
  });

  const { socket, reconnectWebSocket } = useSocket();

  const disconnectSnackbar = useRef();

  const onOpen = useCallback(() => {
    console.log('WebSocket connection with SSD server established');
    closeSnackbar();
    // closeSnackbar(disconnectSnackbar.current);
    enqueueSnackbar(`Connected to ${NODE_ENV} SSD Server`, { variant: 'success' });
  }, [enqueueSnackbar, closeSnackbar]);

  const onClose = useCallback(() => {
    console.log('WebSocket connection with SSD server closed');
    closeSnackbar();
    if (!disconnectSnackbar?.current?.length > 0) {
      disconnectSnackbar.current = enqueueSnackbar(`Disconnected from ${NODE_ENV} SSD Server`, { variant: 'error', persist: true, action: () => (
        <Button variant="text" style={{color: "white"}} onClick={() => {
          reconnectWebSocket();
          closeSnackbar();
        }}>
          Reconnect
        </Button>
      ) });
    };

  }, [closeSnackbar, enqueueSnackbar, reconnectWebSocket]);

  const onMessage = useCallback((message) => {
    const response = JSON.parse(message?.data);

    switch(response?.messageType) {
      case 'ping': // Regular ping message for keeping the connection open
        console.log('SSD server ping received');
        break;

      case 'processSuccess': // If well processed successfully
        let well = response?.payload;

        if (well) {
          if (well.production && well.production?.fluids?.length > 0 && well.details?.['well_type'] !== 'Injection') {
            const color = colors[Math.floor(Math.random() * colors.length)];
            console.log('Generating forecast for well ' + well?.uwi + '...');
            if (!well?.params?.oil?.useWaterflood) {
              dispatch(getForecast({...well, formattedUwi: formatUwi(well?.uwi), color, lastModified: new Date().toLocaleString()})); // Generate multi segment forecast for the well
            } else {
              dispatch(getWaterfloodForecast({...well, color, lastModified: new Date().toLocaleString(), formattedUwi: formatUwi(well?.uwi)}));
            };
          } else if (well.details?.['well_type'] === 'Injection') {
            dispatch(updateWellStatus({uwi: well.uwi, status: 'Injector', details: well?.details}));
            enqueueSnackbar(`Well ${well.uwi} is an injector`, {variant: 'info'});
          } else {
            dispatch(updateWellStatus({uwi: well.uwi, status: 'non-producing', checked: false, details: well?.details}));
            enqueueSnackbar(`Well ${well.uwi} is not producing`, {variant: 'info'});
          };
        };
        break;
      
      case 'typeCurve': // Type curve processed successfully
        closeSnackbar();
        let typeCurve = response?.payload;

        if (typeCurve) {
          dispatch(getTypeCurveForecast({...typeCurve})) // Generate multi segment forecast for the type curve
          .then(res => {
            dispatch(saveTypeCurve(res.payload));
          });
          enqueueSnackbar(`Type curve ${typeCurve.name} processed successfully`, {variant: 'success'});
        };
        break;

      case 'saveTypeCurveStatus': // type curve save status
        try {
          closeSnackbar();
          const typeCurve = response?.payload;
          if (!typeCurve) {
            console.log('Could not save type curve');
            enqueueSnackbar('Could not save type curve', { variant: 'error' });
            break; 
          } else {
            dispatch(saveTypeCurve({...typeCurve, createdBy: user?.value?.email})).then(res => { // save type curve to the database
              if (res && res.payload.success) {
                console.log('Type curve saved successfully');
                enqueueSnackbar('Type curve saved successfully', { variant: 'success' });
              } else {
                console.log('Could not save type curve');
                enqueueSnackbar('Could not save type curve', { variant: 'error' });
              };
            });
            break;
          };
        } catch (error) {
          break;
        };

      case 'groupWell': // Group well processed successfully
        closeSnackbar();
        let groupWell = response?.payload;

        if (groupWell) {
          if (!groupWell.fullCycle) {
            dispatch(getGroupWellForecast({...groupWell})); // Generate multi segment forecast for the prod only group well
          } else {
            dispatch(addGroupWell({...groupWell, checked: true})); // Add the full cycle group well directly
          };
          enqueueSnackbar(`Group well ${groupWell.name} processed successfully`, {variant: 'success'});
        };
        break;

      case 'saveGroupWellStatus': // group well save status
        try {
          closeSnackbar();
          const groupWell = response?.payload;
          if (!groupWell) {
            console.log('Could not save group well');
            enqueueSnackbar('Could not save group well', { variant: 'error' });
            break; 
          } else {
            dispatch(saveGroupWell({...groupWell, createdBy: user?.value?.email})).then(res => { // save group well to the database
              if (res && res.payload.success) {
                console.log('Group well saved successfully');
                enqueueSnackbar('Group well saved successfully', { variant: 'success' });
              } else {
                console.log('Could not save group well');
                enqueueSnackbar('Could not save group well', { variant: 'error' });
              };
            });
            break;
          };
        } catch (error) {
          break;
        };

      case 'localWell': // Local well processed successfully
        let res = response?.payload;
        if (res && res?.well) {
          let well = {...res.well};
          well.status = 'processed';
          if (Object.keys(well?.params)?.length > 0) {
            dispatch(getForecast({...well})); // Generate multi segment forecast for the well
          } else {
            dispatch(updateWellStatus({uwi: well.uwi, status: 'non-producing', checked: false}));
          };
        };
        break;

      // ECONOMICS
      case 'economics': // Economics processed successfully
        try {
          const econRes = response?.payload;
          if (econRes) {

            console.log(econRes);

            closeSnackbar();
            dispatch(updateEconomics({ economics: econRes, projectId: econRes?.projectId, consolidation: econRes?.settings?.consolidation }));
            dispatch(setIsLoadingData(false));
          };
          break;

        } catch (error) {
          break;
        };

      // FBA
      case 'fba': // FBA processed successfully
        const fbaRes = response?.payload;
        if (fbaRes) {

          // convert values from field to metric units
          if (fbaRes?.settings && fbaRes?.settings?.units?.value === 'metric') {
            for (const param of Object.keys(fbaRes?.settings)) {
              let value = fbaRes?.settings?.[param]?.value;

              if (!value) continue;

              if (param === 't_r' || param === 'wht' || param === 'tSep') {
                value = parseInt(f_TO_C(value));
              } else if (param === 'geothermalGradient') {
                value = +(((value) * (5 / 9) * 3.28)?.toFixed(3));
              }  else {
                if (!fbaConversionFactors?.[param]) continue;
                value = fbaRes?.settings?.[param]?.value * fbaConversionFactors?.[param];
              };

              fbaRes.settings[param] = {...fbaRes?.settings?.[param], value};
            };
          };

          dispatch(updateFBA({fbaId: fbaRes.id, fba: fbaRes}));
          dispatch(setIsLoadingData(false));
        };
        break;
      
      case 'saveFBAStatus':
        const fba = response?.payload;
        if (fba) {

          dispatch(saveFBA(fba)).then(res => { // save FBA to the database
            if (res) {
              console.log('FBA saved successfully');
              enqueueSnackbar('FBA saved successfully', { variant: 'success' });
            } else {
              console.log('Could not save FBA');
              enqueueSnackbar('Could not save FBA', { variant: 'error' });
            };
          });
        };
        break;

      case 'deleteFBAStatus':
        const fbaId = response?.payload;
        if (fbaId) {
          dispatch(deleteFBA(fbaId)).then(res => { // save FBA to the database
            if (res) {
              console.log('FBA deleted successfully');
              enqueueSnackbar('FBA deleted successfully', { variant: 'success' });
            } else {
              console.log('Could not delete FBA');
              enqueueSnackbar('Could not delete FBA', { variant: 'error' });
            };
          });
        };
        break;

      case 'processFailed': // If well could not be processed
        let uwi = response?.payload;
        if (uwi?.length > 0) {
          dispatch(updateWellStatus({uwi: uwi, status: 'failed'}));
          enqueueSnackbar(`Failed to process well ${uwi}`, {variant: 'error'});
          console.log('Well ' + uwi + ' could not be processed');
        };
        break;

      case 'localJobFailed':
        console.log('Local job failed!')
        break;

      case 'localJobEnd':
        console.log('Local job ended!');
        break;

      case 'end': // If all wells have been processed
        console.log('All jobs finished!');
        break;
      
      default:
        break;
    };
   
  }, [closeSnackbar, dispatch, enqueueSnackbar, user?.value?.email]);
  
  useEffect(() => {
    if (socket) {
      socket.addEventListener("open", onOpen);
      socket.addEventListener("close", onClose);
      socket.addEventListener("message", onMessage);

      return () => {
        socket.removeEventListener("open", onOpen);
        socket.removeEventListener("close", onClose);
        socket.removeEventListener("message", onMessage);
      };
    };
    
  }, [socket, onOpen, onClose, onMessage]);
  
  useAuthenticate();

  function useAuthenticate() {
    const { user: userAuth0, isAuthenticated, isLoading, loginWithRedirect } = useAuth0();

    const [user, setUser] = useState(null);

    const authenticateUser = useCallback(() => {
      if(isAuthenticated) {
      dispatch(authenticateUserThunk(userAuth0)).then(usr => { // authenticate user
        setUser(usr?.payload);
        let demoUser = true;

        // make sure that non-SSD users get re-routed to production app
        if (
          (window.location.href?.includes('staging') || 
          window.location.href?.includes('development') || 
          window.location.href?.includes('localhost')) && 
          !usr?.payload?.email?.includes('@ssdynamics.com') &&
          !usr?.payload?.email?.includes('@pigmacorp.com')
        ) {
          window.location.href = 'https://app.ssdynamics.com';
        };


        if (usr?.payload?.subscription === 'SSD') { // only authenticate if user has SSD subsbscription
          demoUser = false;
          enqueueSnackbar(`Signed in as ${usr?.payload?.firstName || ""} ${usr?.payload?.lastName || ""}`, {variant: 'info'});
          setSigningIn(false);
        } else if (usr?.payload?.subscription === 'Demo') {
          console.log('Signing in demo user...');
          setSigningIn(false);
          closeSnackbar();
          enqueueSnackbar(`Signed in as demo user`, {variant: 'info', persist: true});
        } else {
          setUserError(true);
          setSigningIn(false);
          enqueueSnackbar(`Could not sign in`, {variant: 'error'});
        };

        const {fbaAccess, rtfoAccess, forecastAccess} = usr?.payload;

        !userError && !demoUser && dispatch(getProjects({email: usr?.payload?.email})).then(res => {  // get user's projects
          const projects = res?.payload?.projects;
          
          if (!projects || projects.length === 0) { // if projects not found
            console.log('Projects not found! Creating a new project...');
            const id = uuid();
            const color = colors[Math.floor(Math.random() * colors.length - 1)];
            const project = {
              id,
              name: 'New Project', 
              color,
              description: 'New project',
              createdBy: usr?.payload?.email,
              createdOn: new Date().toLocaleString(),
              lastModified: new Date().toLocaleString(),
              listIds: [],
              currentListId: "",
              economicsIds: []
            };
            dispatch(addProject({ project: project, lists: [], email: usr?.payload?.email, demoUser: false }));
          };

          projects && projects.length > 0 && projects.forEach(project => { // populate projects with lists and wells
            let lists = {
              byId: {}, 
              allIds: [],
              currentListId: ""
            };
  
            project?.listIds?.forEach(listId => {
              const list = res?.payload?.lists?.filter(list => list.id === listId)?.[0];
              if (list) {
                lists.byId[listId] = {...list, filters: {}, forecastListTab: 0};
                lists.allIds.push(listId);
                list.wellUwis = list.wellUwis.filter(uwi => uwi?.length > 0);
              };
            });
            const listId = project?.listIds?.[project?.listIds?.length - 1];
            lists.currentListId = listId;

            dispatch(addLists({ projectId: project.id, lists: lists }));  // set user's lists
            dispatch(makeListCurrent(listId));
  
            lists?.allIds?.forEach(listId => {
              let uwis = [];
              let wells = {};

              const list = res?.payload?.lists?.find(list => list.id === listId);
              const color = colors[Math.floor(Math.random() * colors.length - 1)];

              if (list) {
                list.wellUwis?.forEach(uwi => {
                  if (uwi?.length > 0) {
                    const well = res?.payload?.wells?.find(well => well.uwi === uwi);
                    wells[uwi] = {
                      uwi,
                      formattedUwi: formatUwi(uwi) || uwi,
                      color: well?.color || color,
                      lastModified: well?.lastModified || new Date().toDateString(),
                      status: 'idle'
                    };
                    uwis.push(uwi);
                  };
                });

                dispatch(addWells({ wells: wells, uwis: uwis, listId: listId }));
              };

            });
          });

          dispatch(setIsLoadingProjectsData(false));
        });

        if (demoUser) { // if demo user
          console.log('Adding demo project');

          const color = colors[Math.floor(Math.random() * colors.length - 1)];
          const demoUwis = ['100141206024W500','100131206024W500','104041706023W500'];
          const list = {
            id: uuid(),
            name: 'Demo List',
            description: 'Demo List',
            color,
            createdBy: usr?.payload?.email,
            createdOn: new Date().toLocaleString(),
            lastModified: new Date().toLocaleString(),
            wellUwis: demoUwis,
            typeCurveId: undefined,
            groupWellId: undefined,
            forecastListTab: 0,
            filters: {}
          };
          const project = {
            id: uuid(),
            name: 'Demo Project', 
            color,
            description: 'Demo project',
            createdBy: usr?.payload?.email,
            createdOn: new Date().toLocaleString(),
            lastModified: new Date().toLocaleString(),
            listIds: [list?.id],
            currentListId: ""
          };
          
          if (socket?.current?.readyState === 1) { // WebSocket needs to be connected        
            socket?.current?.send(JSON.stringify({
              messageType: 'process', 
              payload: demoUwis, 
              userEmail: usr?.payload?.email, 
              params: []
            })); // Send a process request message with a list of UWIs
          };
          
          dispatch(addProject({ project, lists: [list], email: usr?.payload?.email, demoUser})).then(() => {
            dispatch(addLists({ projectId: project.id, lists: {byId: {[list.id]: list}, allIds: [list.id]} }));  // set user's lists
            dispatch(makeListCurrent(list.id));
            let wells = {};
            demoUwis?.forEach(uwi => {
              if (uwi?.length > 0) {
                wells[uwi] = {
                  uwi,
                  formattedUwi: formatUwi(uwi) || uwi,
                  color: color,
                  lastModified: new Date().toDateString(),
                  status: 'idle'
                };
              };
            });

            dispatch(addWells({ wells: wells, uwis: demoUwis, listId: list.id }));
            dispatch(setIsLoadingProjectsData(false));

            
            // if (!options?.processJobs) {
            //   dispatch(setProcessJobs({ 
            //     uwis: demoUwis, 
            //     assistedForecast: false, 
            //     tcParams: false,
            //     localParams: [],
            //     reFit: false
            //   }));
            // };
          });
          

        };


        dispatch(fetchCommodityPrices()); // get price deck
        dispatch(getEconomics(usr?.payload?.email));

        if (!userError && !demoUser) {
          if (forecastAccess) {
              // history.push('/');
          } else if (fbaAccess) {
              history.push('/fba');
          } else if (rtfoAccess) {
              history.push('/rtfo');
          };
        };
      });
    } else {
      loginWithRedirect();
    }
  }, [isAuthenticated, userAuth0, loginWithRedirect]);

    useEffect(() => {
      try {
        if(!isLoading)
          if (!user) { 
            authenticateUser();
          };
        
      } catch (error) {
        console.log('Error signing in');
        enqueueSnackbar(`Could not sign in`, {
          variant: 'error'
        });
        setUserError(true);
        setSigningIn(false);
      };
    }, [user, isLoading, authenticateUser]);
  };
  
  try {
    return (
      <div className="App" css={css`${DarkThemeCss(darkMode === true ? true : false)}`}>
        {
          signingIn ? <SignIn status="signingIn" darkMode={user?.value?.darkMode} /> 
          :
          (user?.value?.subscription && !userError && user?.value?.termsConditions) ? // If user is signed in
            <ThemeProvider theme={theme}>
              <NavDrawer />
              <Switch>
                {Routes.map((route) => (
                  <Route exact path={route.path} key={route.path}>
                    <route.component />
                  </Route>
                ))}
                <Route path={'/wells/:wellUwis'}>
                  <WellsContainer />
                </Route>
                <Route path={'/forecast/:listId'}>
                  <ForecastContainer />
                </Route>
                <Route path={'/rtfo/admin'}>
                  <RTFOAdminContainer />
                </Route>
                <Route path={'/rtfo/:padName'}>
                  <RTFOContainerLoader darkMode={user?.value?.darkMode} />
                </Route>
                
              </Switch>
            </ThemeProvider>
          :
          (user?.value?.subscription && !userError && !user?.value?.termsConditions) ?
          <TermsConditions userEmail={user?.value?.email} buttons={true} />
          :
          <SignIn status="error" darkMode={user?.value?.darkMode} />
        }
        
      </div>
    );
  } catch (error) {
    return <></>
  };
 
};
  

export default App;

